/* eslint-disable no-promise-executor-return */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, CircularProgress } from "@mui/material";
import { SuccessAlertContext } from "App";
import {
  CSVCountCheckOrder,
  CSVManagedItem,
  CSVThresholdCheckStock,
  CSVUploadCountResponseApi,
} from "../types";
// import getCsvThresholdConfirmApi from "../api/getCsvThresholdConfirmApi";
// import getCsvThresholdConfirmCheckApi from "../api/getCsvThresholdConfirmCheckApi";
// import updateThresholdCsv from "../api/uploadThresholdCsv";
import CsvUploadModal from "./CsvUploadModal";
import CSVConfirmTable from "./CsvConfirmTable";
import getCsvConfirmApi from "../api/getCsvConfirmApi";
import getCsvConfirmCheckApi from "../api/getCsvConfirmCheckApi";
import uploadCountCsvApi from "../api/uploadCountCsvApi";

const limit = 10;

/**
 * 確認画面でのアクションボタンたち
 * @param onClose モーダルを閉じる
 * @param onCsvStockUpload アップロードする
 * @param disabled 非活性
 * @returns JSX.Element
 */
function ActionButtons(
  onClose: () => void,
  onCsvStockUpload: () => Promise<void>,
  disabled: boolean,
) {
  return (
    <>
      <Button variant="custom-outlined" onClick={() => onClose()}>
        キャンセル
      </Button>
      <Button variant="contained" onClick={() => onCsvStockUpload()} disabled={disabled}>
        更新
      </Button>
    </>
  );
}

type Props = {
  refresh: () => void; // 在庫を登録した後に読み込み直す
  isOpenStockCsvUpload: boolean;
  setIsOpenStockCsvUpload: Dispatch<SetStateAction<boolean>>;
  isOpenCsvConfirm: boolean;
  setIsOpenCsvConfirm: Dispatch<SetStateAction<boolean>>;
  isOpenThresholdCsvUpload: boolean;
  setIsOpenThresholdCsvUpload: Dispatch<SetStateAction<boolean>>;
  companyCd: string;
};

/**
 * CSVアップロードボタン
 * @param root0 コンポーネントのプロップス
 * @returns JSX.Element
 */
export default function CsvModals({
  refresh,
  isOpenStockCsvUpload,
  setIsOpenStockCsvUpload,
  isOpenCsvConfirm,
  setIsOpenCsvConfirm,
  isOpenThresholdCsvUpload,
  setIsOpenThresholdCsvUpload,
  companyCd,
}: Props) {
  const [isCheck, setIsCheck] = useState(false); // 確認画面かどうか
  const [file, setFile] = useState<File | null>(null);
  const [csvStockList, setCsvStockList] = useState<CSVManagedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false);
  const [processId, setProcessId] = useState("");
  const [initCheckScreen, setInitCheckScreen] = useState(false);
  const [total, setTotal] = useState(0);
  const [confirmError, setConfirmError] = useState<string | undefined>(undefined);

  const { showAlert } = useContext(SuccessAlertContext);

  const onClose = () => {
    setTimeout(() => {
      setIsOpenStockCsvUpload(false);
      setIsOpenThresholdCsvUpload(false);
      setCsvStockList([]);
      setIsCheck(false);
      setFile(null);
      setOffset(0);
      setProcessId("");
      setInitCheckScreen(false);
      setTotal(0);
      setIsLoading(false);
      setConfirmError(undefined);
      isFastLoading.current = false;
    }, 200);
  };

  // CSV反映
  const onStockConfirm = async () => {
    if (file) {
      setIsLoading(true);
      try {
        // CSV反映
        await uploadCountCsvApi({
          process_id: processId,
        });
        onClose();
        showAlert("商品設定を更新しました。");
      } catch (err) {
        console.error(err);
        showAlert("商品設定csvアップロードに失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      showAlert("予期せぬエラーが発生しました。", "error");
    }
  };

  // CSVアップロード
  const onCsvStockUpload = async () => {
    if (file) {
      setIsLoading(true);
      try {
        const csvData = await getCsvConfirmApi({
          code: companyCd,
          limit,
          csv_file: file,
        });

        setCsvStockList(csvData.managed_items);
        setTotal(csvData.total);
        setProcessId(csvData.process_id);
        setOffset(csvData.managed_items.length);
        const wholeErrors = (csvData.errors && csvData.errors.length > 0) ? csvData.errors.join('\n').slice(0,100) : undefined // 画面が埋め尽くされることがあるのでsliceで100文字までにする
        const allErrors = csvData.managed_items.flatMap((s) => s.errors);
        const allErrorStr = (allErrors && allErrors.length > 0) ? allErrors.join('\n').slice(0,100) : undefined;
        setConfirmError((wholeErrors && wholeErrors.length > 0) ? wholeErrors : allErrorStr);
        setIsCheck(true);

        refresh();
      } catch (err) {
        console.error(err);
        showAlert("CSVの確認データ取得に失敗しました。", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // 登録画面追加読み込み
  const getMoreCheckStock = async () => {
    console.log("追加読み込みcheck");
    if (initCheckScreen && total > offset) {
      setIsMoreLoading(true);
      isFastLoading.current = true;
      try {
        const csvData = await getCsvConfirmCheckApi({
          process_id: processId,
          offset,
          limit,
        });
        setOffset((prev) => prev + csvData.length);
        setCsvStockList((prev) => [...prev, ...csvData]);
        // console.log(csvStockList);
      } catch (err) {
        console.error(err);
        showAlert("商品設定CSVの確認データ取得に失敗しました。", "error");
      } finally {
        isFastLoading.current = false;
        setIsMoreLoading(false);
      }
    } else {
      isFastLoading.current = false;
    }
  };

  return (
    <>
      <CsvUploadModal
        isOpenCsvUpload={isOpenStockCsvUpload}
        companyCd={companyCd}
        isCheck={isCheck}
        onClose={onClose}
        onUpload={onCsvStockUpload}
        file={file}
        title="CSVアップロード"
        setFile={setFile}
        isLoading={isLoading}
      />
      <CSVConfirmTable
        open={isOpenStockCsvUpload && isCheck}
        onClose={onClose}
        stockList={csvStockList}
        ActionButtons={ActionButtons(onClose, onStockConfirm, isLoading || !!confirmError)}
        title="商品設定CSV確認"
        content="以下の商品設定を確認します。よろしいですか？"
        isFastLoading={isFastLoading}
        getMoreStockList={getMoreCheckStock}
        setInit={setInitCheckScreen}
        isLoading={isMoreLoading}
        error={confirmError}
        errorTitle="以下のCSVデータにエラーがあります。ご確認ください。"
      />
    </>
  );
}
